import { PatientCondition } from '../types/patientHistory'
import { format } from 'date-fns'
import { MaskedField } from './MaskedField'
import './ConditionsList.css'

interface ConditionsListProps {
  conditions: PatientCondition[]
  patientId?: string
  onEdit: (condition: PatientCondition) => void
  onDelete: (conditionId: string) => Promise<void>
}

export function ConditionsList({ conditions, patientId, onEdit, onDelete }: ConditionsListProps) {
  return (
    <MaskedField
      field="conditions"
      value={conditions}
      patientId={patientId}
      confidentialityLevel="restricted"
    >
      {(canAccess, maskedConditions, requestEmergencyAccess) => {
        if (!canAccess) {
          return (
            <div className="conditions-empty">
              <p>You do not have permission to view condition details.</p>
              {requestEmergencyAccess && (
                <button
                  onClick={requestEmergencyAccess}
                  className="btn-secondary btn-small"
                  style={{ marginTop: '1rem' }}
                >
                  Request Emergency Access
                </button>
              )}
            </div>
          )
        }

        if (maskedConditions.length === 0) {
          return (
            <div className="conditions-empty">
              <p>No conditions recorded yet.</p>
            </div>
          )
        }

  const getStatusBadge = (status: PatientCondition['status']) => {
    const styles = {
      active: { label: 'Active', className: 'badge-active' },
      resolved: { label: 'Resolved', className: 'badge-resolved' },
      chronic: { label: 'Chronic', className: 'badge-chronic' },
      history: { label: 'History', className: 'badge-history' },
    }
    const style = styles[status] || styles.active
    return <span className={`badge ${style.className}`}>{style.label}</span>
  }

  const getSeverityBadge = (severity?: PatientCondition['severity']) => {
    if (!severity) return null
    const styles = {
      mild: { label: 'Mild', className: 'badge-severity-mild' },
      moderate: { label: 'Moderate', className: 'badge-severity-moderate' },
      severe: { label: 'Severe', className: 'badge-severity-severe' },
    }
    const style = styles[severity]
    if (!style) return null
    return <span className={`badge ${style.className}`}>{style.label}</span>
  }

        return (
          <div className="conditions-list">
            {maskedConditions.map((condition: PatientCondition) => (
        <div key={condition.id} className="condition-item">
          <div className="condition-header">
            <div className="condition-title">
              <h3>{condition.condition}</h3>
              {getStatusBadge(condition.status)}
              {getSeverityBadge(condition.severity)}
            </div>
            <div className="condition-actions">
              <button
                className="btn-link"
                onClick={() => onEdit(condition)}
              >
                Edit
              </button>
              <button
                className="btn-link btn-danger"
                onClick={() => {
                  if (window.confirm(`Delete ${condition.condition}?`)) {
                    onDelete(condition.id)
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
          
          <div className="condition-details">
            {condition.icd10Code && (
              <div className="condition-detail">
                <strong>ICD-10:</strong> {condition.icd10Code}
              </div>
            )}
            {condition.diagnosisDate && (
              <div className="condition-detail">
                <strong>Diagnosed:</strong>{' '}
                {format(new Date(condition.diagnosisDate), 'dd MMM yyyy')}
              </div>
            )}
            {condition.onsetDate && condition.diagnosisDate !== condition.onsetDate && (
              <div className="condition-detail">
                <strong>Onset:</strong>{' '}
                {format(new Date(condition.onsetDate), 'dd MMM yyyy')}
              </div>
            )}
            {condition.resolvedDate && (
              <div className="condition-detail">
                <strong>Resolved:</strong>{' '}
                {format(new Date(condition.resolvedDate), 'dd MMM yyyy')}
              </div>
            )}
            {condition.recordedBy && (
              <div className="condition-detail">
                <strong>Recorded by:</strong> {condition.recordedBy}
              </div>
            )}
          </div>

          {condition.notes && (
            <div className="condition-notes">
              <strong>Notes:</strong> {condition.notes}
            </div>
          )}
            </div>
          ))}
          </div>
        )
      }}
    </MaskedField>
  )
}
